import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import PanelCard from './PanelCard';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import InsightDetailDrawer from '../../performance-insights/InsightDetailDrawer';
import ActionFormDrawer from '../../performance-actions/ActionFormDrawer';
import {
  INSIGHT_STATUS_COLORS, SUGGESTED_ACTION_TYPE, insightBody, insightTitle,
} from '../../performance-insights/config';
import { ACTION_STATUS_COLORS } from '../../performance-actions/config';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import {
  Badge, Sub, Table, TabButton, Tabs,
} from '../styles';
import {
  AI_CARDS, LIST_SIZE, LIVE_INSIGHT_STATUSES, REVIEW_RULE, fetchActionCounts, fetchActions, fetchInsights, usePanel,
} from '../config';
import { insightTaskTarget, processHealthHref } from '../../../components/performance/processHealthLinks';
import { hasScopeFilters } from '../../../components/performance/pmAnalyticsFilters';

// Son inceleme (Önemli 4) — bulgu ve aksiyon uçları yalnız dönem süzgeci kabul eder; kapsam süzgeci etkinken kullanıcıya bu
// yüzeylerin tüm şirketi gösterdiği söylenir (aksi halde "Satış" süzgeciyle şirket geneli sayı yan yana okunurdu).
export function UnfilteredNote({ filters }) {
  const { t } = useTranslation();
  return hasScopeFilters(filters) ? <Sub data-note="unfiltered">{t('performance.operations.panel.unfilteredNote')}</Sub> : null;
}

// Spec §6.4 — "İnceleme Bekleyen Çalışanlar" (pm.review.attention, ilk 5; satır menüsü Detay + Aksiyon Aç), "Aksiyon Takibi"
// (Açık / Planlanan / Tamamlanan; sayılar counts ucundan, liste son tarih artan ilk 5) ve "Flexbee AI – Operasyonel İçgörüler"
// (çalışan kapsamlı olmayan canlı bulgular, en çok 3; başlıkta "N yeni bulgu"). Çekmeceler Bulgular sayfasınınkilerle aynıdır.
const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const AiCard = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const AiTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AiBody = styled.div`
  margin: 4px 0 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const AiFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const reasonsOf = (insight) => (insight?.evidence?.reasons || []);

/** Bulgu detayı + aksiyon açma + geçersiz işaretleme — Bulgular sayfasının çekmeceleriyle aynı akış (Süreç Takibi de kullanır). */
export function useInsightDrawers(onChanged) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [invalidFor, setInvalidFor] = useState(null);

  const changeStatus = useCallback(async (id, status, reason) => {
    try {
      await api.patch(`/v1/performance-insights/${id}/status`, { status, reason });
      setDetail(null);
      onChanged?.();
      return true;
    } catch {
      return false;
    }
  }, [onChanged]);

  const createAction = useCallback(async (payload) => {
    try {
      await api.post('/v1/performance-actions', payload);
      setDetail(null);
      onChanged?.();
      return true;
    } catch {
      return false;
    }
  }, [onChanged]);

  const actionDefaults = useMemo(() => (actionFor ? {
    sourceInsightId: actionFor.id,
    periodId: actionFor.periodId,
    actionType: SUGGESTED_ACTION_TYPE[actionFor.ruleCode] || 'custom',
    title: t(`performance.insight.${actionFor.ruleCode}.title`, { defaultValue: actionFor.ruleCode }),
  } : undefined), [actionFor, t]);

  const drawers = (
    <>
      <InsightDetailDrawer
        insight={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onChangeStatus={changeStatus}
        onOpenAction={(row) => setActionFor(row)}
        onInvalidate={(row) => setInvalidFor(row)}
      />
      <ActionFormDrawer open={!!actionFor} defaults={actionDefaults} onClose={() => setActionFor(null)} onSave={createAction} />
      <GenericFormDrawer
        open={!!invalidFor}
        onClose={() => setInvalidFor(null)}
        title={t('performance.insight.invalidTitle')}
        schema={[{ name: 'reason', label: t('performance.insight.invalidReason'), type: 'textarea', required: true, maxLength: 500, rows: 4 }]}
        submitLabel={t('performance.insight.actions.invalidate')}
        onSave={async (form) => changeStatus(invalidFor.id, 'invalid', form.reason)}
      />
    </>
  );
  return { setDetail, setActionFor, drawers };
}

function InsightStatus({ status }) {
  const { t } = useTranslation();
  const c = INSIGHT_STATUS_COLORS[status] || INSIGHT_STATUS_COLORS.new;
  return <StatusBadge $color={c.color} $bg={c.bg}>{t(`performance.insight.statusValue.${status}`, { defaultValue: status })}</StatusBadge>;
}

export function ReviewAttentionPanel({ filters, refreshKey, onChanged }) {
  const { t } = useTranslation();
  const periodId = filters.periodId;
  const [local, setLocal] = useState(0);
  const panel = usePanel(() => fetchInsights({
    'periodId[eq]': periodId, 'ruleCode[eq]': REVIEW_RULE, 'status[in]': LIVE_INSIGHT_STATUSES, pageNumber: 1, pageSize: LIST_SIZE,
  }), [periodId, refreshKey, local], !!periodId);
  const changed = useCallback(() => { setLocal((v) => v + 1); onChanged?.(); }, [onChanged]);
  const { setDetail, setActionFor, drawers } = useInsightDrawers(changed);
  const rows = panel.data?.rows || [];
  return (
    <PanelCard
      panelKey="reviewAttention" title={t('performance.operations.panel.reviewAttention')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.operations.panel.noReview')}
      actions={panel.data ? (
        <Link to={`/performance/insights?ruleCode=${REVIEW_RULE}`} data-link="all">{t('performance.operations.panel.seeAll', { count: panel.data.total })}</Link>
      ) : null}
    >
      <UnfilteredNote filters={filters} />
      <Table>
        <colgroup><col style={{ width: '30%' }} /><col style={{ width: '22%' }} /><col style={{ width: '28%' }} /><col style={{ width: '14%' }} /><col style={{ width: '6%' }} /></colgroup>
        <thead>
          <tr>
            <th>{t('performance.operations.col.employee')}</th>
            <th>{t('performance.operations.col.unit')}</th>
            <th>{t('performance.operations.col.reason')}</th>
            <th>{t('performance.operations.col.status')}</th>
            <th aria-label={t('performance.operations.col.actions')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const reasons = reasonsOf(r);
            const first = reasons[0] ? t(`performance.insight.reason.${reasons[0]}`, { defaultValue: reasons[0] }) : '-';
            return (
              <tr key={r.id} data-row={r.id}>
                <td>{r.names?.[r.scopeId] || '-'}</td>
                <td>{r.scopeDepartmentName || '-'}</td>
                <td title={reasons.map((x) => t(`performance.insight.reason.${x}`, { defaultValue: x })).join(', ')}>
                  {reasons.length > 1 ? `${first} +${reasons.length - 1}` : first}
                </td>
                <td><InsightStatus status={r.status} /></td>
                <td>
                  <OptionsMenu
                    row={r}
                    actions={[
                      { type: 'details', label: t('performance.operations.rowAction.detail'), onClick: () => setDetail(r) },
                      { type: 'open', label: t('performance.operations.rowAction.openAction'), onClick: () => setActionFor(r) },
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {drawers}
    </PanelCard>
  );
}

export const ACTION_TABS = {
  open: 'planned,in_progress',
  planned: 'planned',
  done: 'done',
};

export function ActionTrackingPanel({ filters, refreshKey }) {
  const { t, i18n } = useTranslation();
  const periodId = filters.periodId;
  const [tab, setTab] = useState('open');
  const counts = usePanel(() => fetchActionCounts(periodId), [periodId, refreshKey], !!periodId);
  const list = usePanel(() => fetchActions(periodId, ACTION_TABS[tab]), [periodId, tab, refreshKey], !!periodId);
  const c = counts.data || {};
  const countOf = { open: (c.planned || 0) + (c.inProgress || 0), planned: c.planned || 0, done: c.done || 0 };
  const rows = list.data?.rows || [];
  const error = counts.error || list.error;
  return (
    <PanelCard
      panelKey="actionTracking" title={t('performance.operations.panel.actionTracking')} span={4}
      loading={counts.loading && !counts.data} error={error}
      onRetry={() => { counts.retry(); list.retry(); }}
      actions={<Link to="/performance/actions" data-link="detail">{t('performance.operations.panel.detail')}</Link>}
    >
      <UnfilteredNote filters={filters} />
      <Tabs role="tablist">
        {Object.keys(ACTION_TABS).map((k) => (
          <TabButton key={k} role="tab" aria-selected={tab === k} $active={tab === k} onClick={() => setTab(k)} data-tab={k}>
            {t(`performance.operations.actionTab.${k}`, { count: countOf[k] })}
          </TabButton>
        ))}
      </Tabs>
      {list.loading ? null : rows.length === 0 ? (
        <Sub data-state="empty">{t('performance.operations.panel.noActions')}</Sub>
      ) : (
        <Table>
          <colgroup><col style={{ width: '38%' }} /><col style={{ width: '26%' }} /><col style={{ width: '18%' }} /><col style={{ width: '18%' }} /></colgroup>
          <thead>
            <tr>
              <th>{t('performance.operations.col.action')}</th>
              <th>{t('performance.operations.col.owner')}</th>
              <th>{t('performance.operations.col.due')}</th>
              <th>{t('performance.operations.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const sc = ACTION_STATUS_COLORS[a.status] || ACTION_STATUS_COLORS.planned;
              return (
                <tr key={a.id} data-row={a.id}>
                  <td title={a.title}>{a.title}</td>
                  <td>{a.teamLabel || a.ownerName || '-'}</td>
                  <td>{a.dueDate ? formatDate(a.dueDate, i18n.language) : '-'}</td>
                  <td><StatusBadge $color={sc.color} $bg={sc.bg}>{t(`performance.action.statusValue.${a.status}`, { defaultValue: a.status })}</StatusBadge></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </PanelCard>
  );
}

export function AiInsightsPanel({ filters, refreshKey, onChanged }) {
  const { t, i18n } = useTranslation();
  const periodId = filters.periodId;
  const [local, setLocal] = useState(0);
  const panel = usePanel(async () => {
    const base = { 'periodId[eq]': periodId, 'scopeType[ne]': 'employee', pageNumber: 1 };
    const [live, fresh] = await Promise.all([
      fetchInsights({ ...base, 'status[in]': LIVE_INSIGHT_STATUSES, pageSize: AI_CARDS }),
      fetchInsights({ ...base, 'status[in]': 'new', pageSize: 1 }),
    ]);
    return { rows: live.rows.filter((r) => r.scopeType !== 'employee').slice(0, AI_CARDS), newCount: fresh.total };
  }, [periodId, refreshKey, local], !!periodId);
  const changed = useCallback(() => { setLocal((v) => v + 1); onChanged?.(); }, [onChanged]);
  const { setDetail, drawers } = useInsightDrawers(changed);
  const rows = panel.data?.rows || [];
  return (
    <PanelCard
      panelKey="aiInsights"
      title={(
        <>
          {t('performance.operations.panel.aiInsights')}
          {panel.data?.newCount ? <Badge $tone="info" data-badge="new">{t('performance.operations.panel.newInsights', { count: panel.data.newCount })}</Badge> : null}
        </>
      )}
      span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.operations.panel.noInsights')}
      actions={<Link to="/performance/insights" data-link="all">{t('performance.operations.panel.seeAllPlain')}</Link>}
    >
      <UnfilteredNote filters={filters} />
      {rows.map((r) => {
        // Bulgunun liste bağlantısı (E02 Görevler): kapsamı ve türü süzgeç olarak taşınır (spec §19/10).
        const target = insightTaskTarget(r);
        const list = target ? processHealthHref(filters, target) : null;
        return (
          <AiCard key={r.id} data-insight={r.id}>
            <AiTitle>{insightTitle(r, t)}</AiTitle>
            <AiBody>{insightBody(r, t)}</AiBody>
            <AiFoot>
              <span>{t('performance.operations.panel.insightMeta', { n: r.n, date: formatDate(r.dataAsOf, i18n.language) })}</span>
              <span>
                {list ? <Link to={list} data-link="list">{t('performance.operations.panel.list')}</Link> : null}
                {' '}
                <Button variant="ghost" onClick={() => setDetail(r)} data-action="detail">{t('performance.operations.rowAction.detail')}</Button>
              </span>
            </AiFoot>
          </AiCard>
        );
      })}
      {drawers}
    </PanelCard>
  );
}
